import { filterSteps, paxSettings, locationEnums } from '../appSettings';

/*
  If the selected is already in the selected list - remove it
  If the selected is already in the selected list - remove it
  Only add it if the number of selected items is less than the passed in limit
  Otherwise add it and return a new array
*/
export const handleListUpdate = (newItem, selectedItems) => {
  const alreadySelected = selectedItems.includes(newItem);
      
  // filter out if it is already in the list
  if (alreadySelected) {
    return selectedItems?.filter(selectedItem => selectedItem !== newItem);
  }

  // otherwise add it + make sure we crate a new array, unless we're at the limit of how many choices we allow
  return [...selectedItems, newItem];
};

/*
  Convert a classification code to an id
  Code is for example: AC
  Corresponding id is ACTIVE_TRAINING
*/
export const convertClassificationCodesToIds = (codes, classifications) => {
  /*
    We're running a forEach and conditionally adding the ids provided
    that a matching classification was found. A faulty code could have
    been added manually to the url that won't match a classification
  */
  const mappedIds = []
  codes.forEach((code) => {
    if (code === '') return

    const classificationKeys = Object.keys(classifications)
    const matchingClassificationId = classificationKeys.find((key) => classifications[key].code === code)
    if (matchingClassificationId) {
      mappedIds.push(matchingClassificationId)
    }
  })
  return mappedIds
}

/*
  Convert a classification code to an id
  E.g ACTIVE_TRAINING -> AT
  E.g YOGA -> Y
*/
export const convertClassificationIdsToCodes = (ids, classifications) => {
  return ids.map((id) => {
    /*
      Only look for the code if the Id exists as a property on classifications
      For example - if a a secondary classification has been preselected via url but
      the parent primary classification is not selected - that should result in the
      secondary classification not being set as selected in store.
    */
    if (id === '' || !classifications[id]) {
      return ''
    }

    return classifications[id].code
  })
}


/* 
  Pick the state properties that should be synced to url query pararmeters
*/
export const statePropsForUrlSync = (filters, classifications, hotelProducts, conceptHotels) => {
  const {
    selectedPrimaryClassifications,
    selectedSecondaryClassifications,
    selectedHotelProducts,
    selectedConceptHotels,
  } = filters;
  const pc = convertClassificationIdsToCodes(selectedPrimaryClassifications, classifications);

  /*
    We need to loop though the classifications data to get the classification objects in order to convert
    from complete ids (ACTIVE_TRAINING or YOGA) to codes (AC, Y). Loop through the selected
    primary classifications and put their children in a secondaries object
  */
  let secondaries = {}
  selectedPrimaryClassifications.forEach((primaryKey) => {
    secondaries = {
      ...secondaries,
      ...classifications[primaryKey].children,
    }  
  })
  const sc = convertClassificationIdsToCodes(selectedSecondaryClassifications, secondaries); 

  // hotelProducts works the same way as primary classifications - it's just a different array in the store
  const hp = convertClassificationIdsToCodes(selectedHotelProducts, hotelProducts); 

  const ch = convertClassificationIdsToCodes(selectedConceptHotels, conceptHotels); 

  return {
    pc: pc.join(','), // primary classifications
    sc: sc.join(','), // secondary classifications
    hp: hp.join(','), // hotel products
    pref: filters.selectedPreference,
    temp: filters.selectedTemperature,
    tempPeriod: filters.selectedTemperaturePeriod,
    ft: filters.selectedFlightTime,
    loc: filters.selectedLocations,
    noa: filters.pax.selectedNumberOfAdults,
    noc: filters.pax.selectedNumberOfChildren,
    dep: filters.pax.selectedDeparture,
    depMonths: filters.selectedDepartureMonths ? filters.selectedDepartureMonths.join(',') : null,
    ch: ch.join(','), // concept hotels
    rt: filters.selectedRatings ? filters.selectedRatings.join(',') : null,
  }
}

/*
  map urlQueryParam props to store property names
  E.g AT -> ACTIVE_TRAINING
  E.g Y -> YOGA
*/
export const mapQueryParamFiltersToStore = (urlPropsForStoreSync, classifications, hotelProducts, conceptHotels, departureAirports) => {
  const primaryClassificationCodes = decodeURIComponent(urlPropsForStoreSync.pc).split(',');
  const primaryIds = convertClassificationCodesToIds(primaryClassificationCodes, classifications); // comma-separated in url, array in store

  const secondaryClassificationCodes = decodeURIComponent(urlPropsForStoreSync.sc).split(',');
  let availableSecondaries = {} 
  primaryIds.forEach(primaryId => {
    availableSecondaries = {
      ...availableSecondaries,
      ...classifications[primaryId].children,
    };
  })
  const secondaryIds = convertClassificationCodesToIds(secondaryClassificationCodes, availableSecondaries); // comma-separated in url, array in store
  
  const hotelProductCodes = decodeURIComponent(urlPropsForStoreSync.hp).split(',');
  const hotelProductIds = convertClassificationCodesToIds(hotelProductCodes, hotelProducts); // comma-separated in url, array in store
  const numberOfAdults = parseInt(urlPropsForStoreSync.noa, 10);
  const numberOfChildren = parseInt(urlPropsForStoreSync.noc, 10);
  const conceptHotelCodes = decodeURIComponent(urlPropsForStoreSync.ch).split(',');
  const conceptHotelIds = urlPropsForStoreSync.ch && convertClassificationCodesToIds(conceptHotelCodes, conceptHotels) || []; // comma-separated in url, array in store
  const ratings = urlPropsForStoreSync.rt && decodeURIComponent(urlPropsForStoreSync.rt).split(',') || [];
  const selectedLocations = urlPropsForStoreSync.loc && decodeURIComponent(urlPropsForStoreSync.loc).split(',') || null;

  let departureMonths = null;
  if (urlPropsForStoreSync.depMonths) {
    departureMonths = decodeURIComponent(urlPropsForStoreSync.depMonths).split(',');
  }

  const selectedDepartureAirport = urlPropsForStoreSync.dep || (departureAirports ? departureAirports[0].value : null);
  return {
    selectedPrimaryClassifications: primaryIds,
    selectedSecondaryClassifications: secondaryIds,
    selectedHotelProducts: hotelProductIds,
    selectedPreference: urlPropsForStoreSync.pref,
    selectedTemperature: urlPropsForStoreSync.temp,
    selectedTemperaturePeriod: urlPropsForStoreSync.tempPeriod,
    selectedFlightTime: urlPropsForStoreSync.ft,
    selectedLocations,
    pax: {
      selectedNumberOfAdults: Number.isNaN(numberOfAdults) ? paxSettings.defaultSelectedNumberOfAdults : numberOfAdults,
      selectedNumberOfChildren: Number.isNaN(numberOfChildren) ? paxSettings.defaultSelectedNumberOfChildren : numberOfChildren,
      selectedDeparture: selectedDepartureAirport,
      selectedDepartureCaIdDict: selectedDepartureAirport ? departureAirports.find(airport => airport.value.includes(selectedDepartureAirport))?.caIdDict : null,
    },
    selectedDepartureMonths: departureMonths,
    selectedConceptHotels: conceptHotelIds,
    selectedRatings: ratings,
  }
}

/*
  Parse the url query params (e.g ?pc=AT&sc=Y&region=europe)
  This translates to store properties:
    * selectedPrimaryClassifications = ['ACTIVE_TRAINING']
    * selectedSecondaryClassifications = ['YOGA']
    * regions = 'EUROPE'
*/
export const parseInitUrlFilterState = (urlQueryParams, classifications, hotelProducts, conceptHotels, departureAirports) => {
  const mappedToStoreFilters = mapQueryParamFiltersToStore(urlQueryParams, classifications, hotelProducts, conceptHotels, departureAirports);
  return mappedToStoreFilters
}

/*
  initRoute is the current hash-route (e.g https://ving.se#/secondary-classifications)
  In this example, initRoute is secondary-classifications

  In filterSteps (see appSettings.js) - each step holds an index - the step in the filter flow
*/
export const getStepByInitRoute = (initRoute) => {
  return filterSteps.find(step => step.id === initRoute);
}

/* 
  Return an array of picked locations. If the user clicks "Anywhere" all should be selected,
  unless all are already selected, then all should be unselected.
  If all locations are selected one by one, then anywhere should be marked as active when all are active
  but be unselected if a single one then is unselected.
*/
export const getUpdatedSelectedLocations = (clickedLocation, selectedLocations) => {
  let anywhereToggle = null
  let updatedSelectedLocations = null
  const locationEnumList = Object.keys(locationEnums);
  if (clickedLocation === locationEnums.ANYWHERE) {
    const anywhereActive = selectedLocations?.includes(locationEnums.ANYWHERE) || false;
    const allLocationsSelected = selectedLocations && selectedLocations?.length === locationEnumList?.length;
    anywhereToggle = anywhereActive && allLocationsSelected  ? null : locationEnumList.map(locationkey => locationkey);
  } else {
    updatedSelectedLocations = selectedLocations && handleListUpdate(clickedLocation, selectedLocations) || [clickedLocation];
    if (updatedSelectedLocations?.includes(clickedLocation)) {
      // if an item was selected, we should check if all locations are chcked, in that case ANYWHERE should be automatically checked aswell
      // length -1 since we're not counting "ANYWHERE".
      if (updatedSelectedLocations?.length === locationEnumList?.length - 1) {
        updatedSelectedLocations.push(locationEnums.ANYWHERE)
      }
    } else {
      // if the clicked location was filtered out of the selected list (i.e unselected, we should always unselect ANYWHERE)
      updatedSelectedLocations = updatedSelectedLocations.filter(location => location !== locationEnums.ANYWHERE)
    }
  }

  return anywhereToggle || updatedSelectedLocations;
}
